import { queryOptions } from '@tanstack/react-query';
import { graphql } from '#/lib/gql';
import { fetchGraphQL } from '#/lib/graphql';
import type { LocationLabel } from '#/lib/location';

const FIND_ALL_PLACES_QUERY = graphql(`
  query FindAllPlaces($spaceId: String!, $page: Int!, $perPage: Int!) {
    place(spaceId: $spaceId) {
      findNewest(
        pagination: { page: $page, perPage: $perPage }
        surface: OverviewPage
      ) {
        edges {
          node {
            id
            name
            description
            slug
            path
            cover {
              cardUrl
              posterUrl
            }
            citySlug
            districtSlug
            labels
            labelDetails {
              slug
            }
            embeds {
              reels {
                publishedAt
              }
            }
          }
        }
        sortOrder
        paginationMeta {
          currentPage
          totalPages
          totalCount
          hasPreviousPage
          hasNextPage
        }
        availableCategories {
          slug
          name
        }
        availableTags {
          slug
          name
        }
      }
    }
  }
`);

export type PlacesPagination = {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
};

export type AllPlace = {
  id: string;
  name: string;
  description: string;
  slug: string;
  href: string;
  cover: string;
  poster: string;
  citySlug: string;
  cityLabel: string;
  districtSlug: string;
  districtLabel: string;
  labels: string[];
  labelSlugs: string[];
  latestReel: {
    publishedAt: string | null;
  } | null;
};

export type AllPlacesResult = {
  places: AllPlace[];
  sortOrder: string;
  pagination: PlacesPagination;
  tags: { slug: string; label: string }[];
};

function slugToLabel(slug: string): string {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

async function fetchAllPlaces(
  spaceId: string,
  page: number,
  perPage: number,
  locationLabels: Map<string, LocationLabel>,
): Promise<AllPlacesResult> {
  const data = await fetchGraphQL(FIND_ALL_PLACES_QUERY, {
    spaceId,
    page,
    perPage,
  });

  const places = (data?.place?.findNewest?.edges ?? []).flatMap((edge) => {
    if (!edge?.node) return [];
    const city = locationLabels.get(edge.node.citySlug);
    const districtLabel =
      city?.districts.get(edge.node.districtSlug) ??
      slugToLabel(edge.node.districtSlug);
    const latestReel = edge.node.embeds?.reels?.[0];

    return [
      {
        id: edge.node.id,
        name: edge.node.name,
        description: edge.node.description ?? '',
        slug: edge.node.slug,
        href: edge.node.path,
        cover: edge.node.cover?.cardUrl ?? '',
        poster: edge.node.cover?.posterUrl ?? '',
        citySlug: edge.node.citySlug,
        cityLabel: city?.city ?? slugToLabel(edge.node.citySlug),
        districtSlug: edge.node.districtSlug,
        districtLabel,
        labels: (edge.node.labels ?? []).filter((label): label is string =>
          Boolean(label),
        ),
        labelSlugs: (edge.node.labelDetails ?? []).flatMap((label) =>
          label?.slug ? [label.slug] : [],
        ),
        latestReel: latestReel
          ? { publishedAt: latestReel.publishedAt ?? null }
          : null,
      } satisfies AllPlace,
    ];
  });

  const meta = data?.place?.findNewest?.paginationMeta;
  const availableCategories =
    data?.place?.findNewest?.availableCategories ?? [];
  const availableTags = data?.place?.findNewest?.availableTags ?? [];

  return {
    places,
    sortOrder: data?.place?.findNewest?.sortOrder ?? 'Newest',
    pagination: {
      currentPage: meta?.currentPage ?? 1,
      totalPages: meta?.totalPages ?? 1,
      totalCount: meta?.totalCount ?? places.length,
      hasPreviousPage: meta?.hasPreviousPage ?? false,
      hasNextPage: meta?.hasNextPage ?? false,
    },
    tags: [...availableCategories, ...availableTags].flatMap((label) =>
      label ? [{ slug: label.slug, label: label.name }] : [],
    ),
  };
}

async function fetchAllPlacePages(
  spaceId: string,
  perPage: number,
  locationLabels: Map<string, LocationLabel>,
): Promise<AllPlacesResult> {
  const first = await fetchAllPlaces(spaceId, 1, perPage, locationLabels);

  // The API caps perPage server-side (currently 100), so a single request
  // does not necessarily return every place even when perPage is set high.
  // Fetch the remaining pages so filters, facets and the map see the full set.
  const remainingPages = Array.from(
    { length: Math.max(0, first.pagination.totalPages - 1) },
    (_, index) => index + 2,
  );
  const rest = await Promise.all(
    remainingPages.map((page) =>
      fetchAllPlaces(spaceId, page, perPage, locationLabels),
    ),
  );

  return {
    places: [first, ...rest].flatMap((result) => result.places),
    sortOrder: first.sortOrder,
    pagination: {
      ...first.pagination,
      currentPage: first.pagination.totalPages,
      hasNextPage: false,
    },
    tags: first.tags,
  };
}

export const allPlacesQueries = {
  places: (
    spaceId: string,
    perPage = 100,
    locationLabels = new Map<string, LocationLabel>(),
  ) =>
    queryOptions({
      queryKey: ['places', 'all', spaceId, perPage] as const,
      queryFn: () => fetchAllPlacePages(spaceId, perPage, locationLabels),
    }),
};

const GET_FEATURED_EMBED_QUERY = graphql(`
  query GetFeaturedEmbed($spaceId: String!, $slug: String!) {
    place(spaceId: $spaceId) {
      getBySlug(slug: $slug, surface: DetailPage) {
        embeds {
          reels {
            url
            format
            publishedAt
          }
        }
      }
    }
  }
`);

export type FeaturedEmbed = {
  url: string;
  format: string;
  publishedAt: string | null;
} | null;

async function fetchFeaturedEmbed(
  spaceId: string,
  slug: string,
): Promise<FeaturedEmbed> {
  const data = await fetchGraphQL(GET_FEATURED_EMBED_QUERY, { spaceId, slug });
  const reel = data?.place?.getBySlug?.embeds?.reels?.[0];

  return reel
    ? { url: reel.url, format: reel.format, publishedAt: reel.publishedAt }
    : null;
}

export const featuredEmbedQueries = {
  embed: (spaceId: string, slug: string) =>
    queryOptions({
      queryKey: ['places', 'featured-embed', spaceId, slug] as const,
      queryFn: () => fetchFeaturedEmbed(spaceId, slug),
    }),
};
